import { titleFormat } from "./go"
import { getPathSuffixWithoutLocale, normalizePathname } from "./localePath"

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** 将 URL 中的 slug 片段规范化为与 titleFormat 一致（小写、空格转 -、去除特殊符号） */
export function normalizeSlugFromUrl(slugPart: string): string {
  const decoded = safeDecodeURIComponent(slugPart)
  const formatted = titleFormat(decoded, { lower: true })
  return formatted || decoded.toLowerCase()
}

/** 品目路径段规范化（小写 + slug 规则，与 redirect-by-id 一致） */
export function normalizeItemSlug(item: string): string {
  return normalizeSlugFromUrl(item)
}

/** 生成详情页 id-slug 路径段，例如 `123-copper-price`。slug 为空时回退到页面 ID */
export function buildIdSlugSegment(id: number | string, title: string): string {
  const slug = titleFormat(title, { lower: true })
  return slug ? `${id}-${slug}` : `${id}`
}

/**
 * 将路径段编码为可写入 HTTP Location 头的安全形式。
 * Node.js 要求 header 值为 ASCII，非拉丁字符（如韩文、中文）须 percent-encode。
 */
export function encodePathSegmentForLocation(segment: string): string {
  return encodeURIComponent(segment)
}

export interface BuildIdSlugRedirectPathOptions {
  locale: string
  defaultLocale: string
  id: number | string
  title: string
  /** 路由前缀，如 newscontent、announcements */
  routePrefix?: string | string[]
  /**
   * www 反向代理挂载段（如 webinar、industry）。
   * 由调用方判断当前请求是否经 www 反代访问后传入；独立域名 / 本地直连应留空，
   * 否则会把 /xxx 错跳为 /webinar/xxx（在独立域名下不存在该路径）。
   */
  mountSegment?: string
}

function normalizeRoutePrefixParts(prefix?: string | string[]): string[] {
  if (!prefix) return []
  const parts = Array.isArray(prefix) ? prefix : prefix.split("/")
  return parts.map((part) => part.trim()).filter(Boolean)
}

/**
 * 判断当前 id-slug 路由段是否与后端标题生成的 canonical 段不一致。
 * 当前 URL 只做 URI 解码参与比较，不根据错误 slug 做清洗或修正。
 */
export function isIdSlugSegmentMismatch(currentIdName: string, id: number | string, title: string): boolean {
  return safeDecodeURIComponent(currentIdName) !== buildIdSlugSegment(id, title)
}

/** 构建 id-slug 详情页 canonical 重定向路径（支持 www 子项目挂载段） */
export function buildIdSlugRedirectPath({
  locale,
  defaultLocale,
  id,
  title,
  routePrefix,
  mountSegment,
}: BuildIdSlugRedirectPathOptions): string {
  const parts: string[] = []

  if (locale !== defaultLocale) {
    parts.push(encodeURIComponent(locale))
  }

  const normalizedMount = mountSegment?.replace(/^\/+|\/+$/g, "")
  if (normalizedMount) {
    parts.push(encodeURIComponent(normalizedMount))
  }

  parts.push(...normalizeRoutePrefixParts(routePrefix).map((part) => encodeURIComponent(part)))
  parts.push(encodePathSegmentForLocation(buildIdSlugSegment(id, title)))

  return `/${parts.join("/")}`
}

/** 构建 id-slug 详情页的 canonical 重定向路径（用于 permanentRedirect / redirect） */
export function buildDetailRedirectPath(
  locale: string,
  routePrefix: string,
  id: number | string,
  title: string,
  defaultLocale: string = "en"
): string {
  return buildIdSlugRedirectPath({ locale, defaultLocale, id, title, routePrefix })
}

function normalizeCanonicalSegment(segment: string): string {
  const decoded = safeDecodeURIComponent(segment)
  const idSlugMatch = decoded.match(/^(\d+)-(.*)$/)
  if (idSlugMatch) {
    const id = idSlugMatch[1] ?? ""
    const slugPart = idSlugMatch[2] ?? ""
    const slug = titleFormat(slugPart, { lower: true })
    return slug ? `${id}-${slug}` : id
  }

  const formatted = titleFormat(decoded, { lower: true })
  return formatted || decoded.toLowerCase()
}

/**
 * 将 pathname 转为 canonical / hreflang 使用的路径后缀：
 * 小写、无 query、slug 段按 titleFormat 规范化（忽略特殊符号与空格）
 */
export function normalizeSeoPathSuffix(pathname: string): string {
  const suffix = getPathSuffixWithoutLocale(normalizePathname(pathname))
  if (!suffix) return ""

  const segments = suffix.split("/").filter(Boolean)
  if (segments.length === 0) return ""

  return `/${segments.map(normalizeCanonicalSegment).join("/")}`
}
